import { initializeApp, FirebaseApp } from "firebase/app";

import { get, getDatabase, ref, child, onValue } from "firebase/database";
import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendEmailVerification,
  isSignInWithEmailLink,
  signInWithEmailLink,
  applyActionCode,
  Auth,
  UserCredential,
  User,
  signInAnonymously,
  updateProfile,
} from "firebase/auth";

import {
  ref as strogeRef,
  getDownloadURL,
  getStorage,
  uploadBytes,
} from "firebase/storage";

import { getAnalytics } from "firebase/analytics";

import request from "./request";
import { actionTypes } from "../redux/firebaseRedux";
import { store } from "../redux/store";

let app: FirebaseApp,
  auth: Auth,
  db,
  storage = null,
  storageBucket: string;

const { REACT_APP_API_URL, REACT_APP_API_VERSION } = process.env;

const baseURL = `${REACT_APP_API_URL}/api/${REACT_APP_API_VERSION}`;

let firebaseConfigInfo = null;

(async () => {
  const resp = await request("GET", `${baseURL}/config/firebase`);

  app = initializeApp(resp.data);
  firebaseConfigInfo = resp.data;
  storageBucket = resp.data?.storageBucket;
  auth = getAuth(app);

  db = getFirestore(app);
  storage = getStorage(app);

  store.dispatch({ type: actionTypes.FIREBASE_INITAILIZE, payload: true });

  getAnalytics();
})();

const getAuthInstance = (): Auth => {
  if (auth) return auth;

  auth = getAuth(app);
  return auth;
};

const signInWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    throw err.code;
  }
};

const signInAnonymous = async (values: any): Promise<any> => {
  try {
    const res = await signInAnonymously(auth);

    const trackRef = collection(db, "tracks");
    const q = query(trackRef, where("default", "==", true));

    const querySnapshot = await getDocs(q);

    const docData = [];
    querySnapshot.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });
    values.track = docData[0].docId;
    await setDoc(doc(db, "users", res.user.uid), {
      disabled: false,
      ...values,
    });

    await updateUserCredentials(values.displayName);

    return res;
  } catch (err) {
    throw err.code;
  }
};

const signInOnBoard = async (): Promise<any> => {
  try {
    const res = await signInAnonymously(auth);

    return res;
  } catch (err) {
    throw err.code;
  }
};

const sendVerificationEmail = async (user: User): Promise<void> => {
  try {
    return await sendEmailVerification(user);
  } catch (e) {
    console.log(e);
    throw e.code;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const registerWithEmailAndPassword = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string
) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);

    await sendVerificationEmail(res.user);
    const user = res.user;

    await addDoc(collection(db, "users"), {
      uid: user.uid,
      displayName: firstName + " " + lastName,
      firstName,
      lastName,
      authProvider: "local",
      email,
      disabled: false,
      emailVerified: user.emailVerified,
    });

    await updateUserCredentials(firstName + " " + lastName);
    return res;
  } catch (err) {
    console.error(err.code);
    throw err.code;
  }
};

const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err) {
    console.error(err);
  }
};

const getSessionDoc = async (sessionId: string): Promise<any> => {
  try {
    const docRef = doc(db, "sessions", sessionId);
    const sessionDoc = await getDoc(docRef);

    return { docId: sessionDoc.id, ...sessionDoc.data() };
  } catch (e) {
    console.log(e);
  }
};

const fetchTrackDetails = async (trackName = "default"): any => {
  const trackRef = collection(db, "tracks");
  const q = query(trackRef, where("acl", "==", trackName));

  const querySnapshot = await getDocs(q);

  const docData = [];
  querySnapshot.forEach((doc) => {
    docData.push({
      docId: doc.id,
      ...doc.data(),
    });
  });

  return docData;
};

const fetchQuestions = async (
  sessionId: string,
  uid: string,
  moderation: string = ""
): Promise<any> => {
  try {
    const sessionRef = collection(db, "sessions", sessionId, "questions");
    let q = {};
    if (moderation === "closed") {
      q = query(
        sessionRef,
        where("uid", "==", uid),
        orderBy("createdAt", "asc")
      );
    } else {
      q = query(
        sessionRef,
        where("isVisible", "==", true),
        orderBy("createdAt", "asc")
      );
    }
    const querySnapshot = await getDocs(q);
    const docData = [];
    querySnapshot.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });

    return docData;
  } catch (e) {
    console.log(e);
  }
};

const insertQuestion = async (sessionId: string, obj: any): Promise<any> => {
  const resp = await addDoc(
    collection(db, "sessions", sessionId, "questions"),
    {
      uid: await auth.currentUser.uid,
      createdAt: serverTimestamp(), //Date.now(),
      ...obj,
    }
  );
  return resp;
};

const getUserAnswers = async (
  pollId: string,
  sessionId: string
): Promise<any> => {
  try {
    const pollsDB = getDatabase(
      app,
      `https://${firebaseConfigInfo.projectId}-polls.europe-west1.firebasedatabase.app/`
    );
    const dbRef = ref(pollsDB);

    const documentPath = `${pollId}/${sessionId}`;

    const snapshot = await get(
      child(dbRef, `${documentPath}/answers/${auth.currentUser.uid}`)
    );

    if (snapshot.exists()) {
      const { answer } = snapshot.val() || { answer: null };
      return answer;
    }

    return null;
  } catch (e) {
    console.log(e);
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const fetchPollResultsByPollId = async (pollId: string, sessionId: string) => {
  try {
    const sessionRef = collection(
      db,
      "polls",
      pollId,
      "sessions",
      sessionId,
      "results"
    );
    const q = query(
      sessionRef,
      where("sessionId", "==", sessionId),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const resultQuery = await getDocs(q);
    const docData = [];
    resultQuery.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });

    return [...docData];
  } catch (e) {
    console.log(e);
  }
};

const fetchUserInfoByEmailId = async (email: string): Promise<any> => {
  try {
    const docRef = collection(db, "users");
    const q = query(docRef, where("email", "==", email));

    const resultQuery = await getDocs(q);

    const docData = [];
    resultQuery.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });

    // console.log([...docData]);
    return [...docData];
  } catch (e) {
    console.log(e);
  }
};

const getUserInfoByParticipantId = async (
  participantId: string
): Promise<any> => {
  try {
    const docRef = collection(db, "users");
    const q = query(docRef, where("participantId", "==", participantId));

    const resultQuery = await getDocs(q);

    const docData = [];
    resultQuery.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });

    return (docData.length && docData[0]) || null;
  } catch (e) {
    console.log(e);
  }
};

const fetchUserInfoByUId = async (uid: string): Promise<any> => {
  try {
    const docRef = collection(db, "users");
    const q = query(docRef, where("uid", "==", uid));

    const resultQuery = await getDocs(q);

    const docData = [];
    resultQuery.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });

    // console.log([...docData]);
    return [...docData];
  } catch (e) {
    console.log(e);
  }
};

const insertPreRegisterUser = async (userObj) => {
  return await addDoc(collection(db, "users"), userObj);
};

const getStorageFileUrl = async (url): any => {
  return await getDownloadURL(strogeRef(storage, url));
};

const getUserInfoByEmail = async (email: string): Promise<any> => {
  try {
    const docRef = collection(db, "users");
    const q = query(docRef, where("email", "==", email));

    const resultQuery = await getDocs(q);

    const docData = [];
    resultQuery.forEach((doc) => {
      docData.push({
        docId: doc.id,
        ...doc.data(),
      });
    });

    // console.log([...docData]);
    return [...docData];
  } catch (e) {
    console.log(e);
  }
};

const updateUserCredentials = async (displayName: string): Promise<any> => {
  return await updateProfile(auth.currentUser, { displayName });
};

const logout = (): void => {
  auth.signOut();
};

const fetchStreamInfoById = async (streamId: string): Promise<any> => {
  try {
    const docRef = doc(db, "streams", streamId);
    const _doc = await getDoc(docRef);

    return { docId: _doc.id, ..._doc.data() };
  } catch (e) {
    console.log(e);
  }
};

const uploadFileToStorage = async (file, fileName = ""): Promise<any> => {
  const randomKey = new Date().getTime().toString(36);
  const _fileName = `${randomKey}_${fileName}`;
  const storageRef = strogeRef(storage, "uploads/" + _fileName);
  const storageResponse = await uploadBytes(storageRef, file);

  return (
    "gs://" +
    storageResponse.metadata.bucket +
    "/" +
    storageResponse.metadata.fullPath
  );
};

const getMicrositeWidgetById = async (
  docName: string,
  id: string
): Promise<any> => {
  const docRef = doc(db, "microsite", "layout", docName, id);

  const querySnapshot = await getDoc(docRef);
  const _data = querySnapshot.data();

  return { docId: id, ..._data };
};

const fetchPollInfoById = async (pollId: string): Promise<any> => {
  try {
    const docRef = doc(db, "polls", pollId);
    const sessionDoc = await getDoc(docRef);

    return { docId: sessionDoc.id, ...sessionDoc.data() };
  } catch (e) {
    console.log(e);
  }
};

const getBucketDownloadUrl = async (url: string): Promise<any> => {
  return await await getDownloadURL(strogeRef(storage, url));
};

const updatePollResult = async (pollId, sessionId, callback) => {
  const pollsDB = getDatabase(
    app,
    `https://${firebaseConfigInfo.projectId}-polls.europe-west1.firebasedatabase.app/`
  );

  const _ref = ref(pollsDB, `${pollId}/${sessionId}/results`);
  onValue(_ref, (snapshot) => {
    const data = snapshot.val();
    callback(data);
  });
};

const onSnapshotMicrositeLayout = (callBack: Function): Function => {
  return onSnapshot(doc(db, "microsite", "layout"), (dt) => {
    // console.log("microsite updated");
    const { widgets }: any = { ...dt.data() };

    const _widgets = !!widgets
      ? Object.values(widgets)?.map((d) => ({ ...d }))
      : [];

    callBack(_widgets || []);
  });
};

const onSnapshotGeneralSettings = (callback: Function): Function => {
  return onSnapshot(doc(db, "settings", "general"), (dt) => {
    // console.log("general setting updated ");
    callback(dt.data());
  });
};

const onSnapshotRegistrationSettings = (callback: Function): Function => {
  return onSnapshot(doc(db, "settings", "registration"), (dt) => {
    // console.log("registration setting updated");
    callback(dt.data());
  });
};

const onSnapshotTrackDetails = (
  trackId: string,
  callback: Function
): Function => {
  const q = doc(db, "tracks", trackId);

  return onSnapshot(q, (dt) => {
    callback((dt.data() && { docId: dt.id, ...dt.data() }) || []);
  });
};

const onSnapshotSessionDetails = (
  sessionId: string,
  callback: Function
): Function => {
  return onSnapshot(doc(db, "sessions", sessionId), (doc) => {
    // console.log("sessions updated");
    callback({
      docId: doc.id,
      ...doc.data(),
    });
  });
};

const onSnapshotSessionQuestion = (
  sessionId: string,
  moderation: string,
  uid: string,
  callback: Function
): Function => {
  const sessionRef = collection(db, "sessions", sessionId, "questions");
  let q = {};
  if (moderation === "closed") {
    q = query(sessionRef, where("uid", "==", uid), orderBy("createdAt", "asc"));
  } else {
    q = query(sessionRef, orderBy("createdAt", "asc"));
  }

  return onSnapshot(q, (snapshot) => {
    const docData = [];
    snapshot.forEach((dt) => {
      docData.push({
        docId: dt.id,
        ...dt.data(),
      });
    });

    callback(docData);
  });
};

const onSnapshotPollDetails = (
  pollId: string,
  callback: Function
): Function => {
  return onSnapshot(doc(db, "polls", pollId), (doc) => {
    // console.log("polls updated");
    callback({
      docId: doc.id,
      ...doc.data(),
    });
  });
};

const onSnapshotUser = (userId: string, callback: Function): Function => {
  return onSnapshot(doc(db, "users", userId), (doc) => {
    // console.log("users updated");
    callback({ uid: doc.id, ...doc.data() });
  });
};

export {
  auth,
  storage,
  storageBucket,
  serverTimestamp,
  signInWithEmail,
  registerWithEmailAndPassword,
  resetPassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  getAuthInstance,
  sendVerificationEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  applyActionCode,
  getSessionDoc,
  onSnapshot,
  doc,
  fetchTrackDetails,
  fetchQuestions,
  insertQuestion,
  collection,
  getUserAnswers,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  logout,
  fetchPollResultsByPollId,
  fetchUserInfoByEmailId,
  getUserInfoByParticipantId,
  signInAnonymous,
  fetchUserInfoByUId,
  getStorageFileUrl,
  insertPreRegisterUser,
  getUserInfoByEmail,
  signInOnBoard,
  updateUserCredentials,
  Timestamp,
  fetchStreamInfoById,
  sendEmailVerification,
  uploadFileToStorage,
  getBucketDownloadUrl,
  getMicrositeWidgetById,
  fetchPollInfoById,
  updatePollResult,
  onSnapshotGeneralSettings,
  onSnapshotRegistrationSettings,
  onSnapshotTrackDetails,
  onSnapshotSessionDetails,
  onSnapshotPollDetails,
  onSnapshotMicrositeLayout,
  onSnapshotUser,
  onSnapshotSessionQuestion,
};
